import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { OnboardingStepProps } from './types';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, Check, X, Lock, Shield } from 'lucide-react';

interface PasswordRequirement {
  regex: RegExp;
  text: string;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { regex: /.{8,}/, text: 'At least 8 characters long' },
  { regex: /[A-Z]/, text: 'Contains uppercase letter' },
  { regex: /[a-z]/, text: 'Contains lowercase letter' },
  { regex: /[0-9]/, text: 'Contains at least one number' },
  { regex: /[!@#$%^&*]/, text: 'Contains special character (!@#$%^&*)' },
];

const PasswordCreation: React.FC<OnboardingStepProps> = ({ onNext, onBack, isFirstStep, isLastStep }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { toast } = useToast();

  const checkPasswordRequirement = (requirement: PasswordRequirement): boolean => {
    return requirement.regex.test(password);
  };

  const getPasswordStrength = (): { score: number; label: string; color: string } => {
    const metRequirements = PASSWORD_REQUIREMENTS.filter(req => checkPasswordRequirement(req)).length;
    
    if (metRequirements === 5) return { score: 100, label: 'Strong', color: 'text-green-600' };
    if (metRequirements >= 4) return { score: 80, label: 'Good', color: 'text-blue-600' };
    if (metRequirements >= 3) return { score: 60, label: 'Fair', color: 'text-yellow-600' };
    if (metRequirements >= 2) return { score: 40, label: 'Weak', color: 'text-orange-600' };
    return { score: 20, label: 'Very Weak', color: 'text-red-600' };
  };

  const passwordStrength = getPasswordStrength();

  const handleCreatePassword = () => {
    if (!password || !confirmPassword) {
      toast({
        title: "Fields Required",
        description: "Please fill in both password fields.",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please ensure both passwords match.",
        variant: "destructive",
      });
      return;
    }

    const unmetRequirements = PASSWORD_REQUIREMENTS.filter(req => !checkPasswordRequirement(req));
    if (unmetRequirements.length > 0) {
      toast({
        title: "Password Requirements Not Met",
        description: `Please ensure your password meets all requirements.`,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Password Created Successfully!",
      description: "Your account is now secured with a strong password.",
    });

    onNext();
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow-sm">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-vitl-green/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-vitl-green" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Create a Strong Password</h3>
          <p className="text-gray-600">
            Your password is the first line of defense for your account. Make it strong!
          </p>
        </div>
        
        <div className="space-y-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          {password && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Password Strength:</span>
                <span className={`font-medium ${passwordStrength.color}`}>
                  {passwordStrength.label}
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-300 ${
                    passwordStrength.score === 100 ? 'bg-green-500' :
                    passwordStrength.score >= 80 ? 'bg-blue-500' :
                    passwordStrength.score >= 60 ? 'bg-yellow-500' :
                    passwordStrength.score >= 40 ? 'bg-orange-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${passwordStrength.score}%` }}
                />
              </div>
            </div>
          )}
          
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
            )}
            {confirmPassword && password === confirmPassword && (
              <p className="mt-1 text-sm text-green-600">Passwords match</p>
            )}
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="h-4 w-4 text-vitl-green" />
              <p className="text-sm font-medium text-gray-700">Password Requirements:</p>
            </div>
            <ul className="space-y-2">
              {PASSWORD_REQUIREMENTS.map((req, index) => {
                const isMet = password ? checkPasswordRequirement(req) : false;
                return (
                  <li key={index} className="flex items-center gap-2 text-sm">
                    {isMet ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <X className="h-4 w-4 text-gray-400" />
                    )}
                    <span className={isMet ? 'text-green-600' : 'text-gray-600'}>
                      {req.text}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          
          <div className="flex justify-between gap-4">
            {!isFirstStep ? (
              <Button
                onClick={onBack}
                variant="outline"
                className="px-8"
              >
                Back
              </Button>
            ) : (
              <div></div>
            )}
            <Button
              onClick={handleCreatePassword}
              className="bg-vitl-green hover:bg-vitl-green/90 text-white px-8"
              disabled={!password || !confirmPassword || password !== confirmPassword || passwordStrength.score < 100}
            >
              Create Password
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordCreation;